import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { fetchCreditBalance } from "@/store/slices/aiCreditsSlice";
import { cn } from "@/utils/cn";

function formatResetDate(iso?: string, locale = "fr-FR"): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "long",
    }).format(new Date(iso));
  } catch {
    return "";
  }
}

interface QuotaIndicatorProps {
  variant?: "banner" | "inline";
}

export const QuotaIndicator: React.FC<QuotaIndicatorProps> = ({
  variant = "banner",
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const quota = useAppSelector((s) => s.aiChat.quotaStatus);
  const creditsBalance = useAppSelector((s) => s.aiCredits.balance);

  // Le solde de crédits n'arrive pas par le flux SSE du chat : chargé une
  // fois ici pour pouvoir l'afficher à côté du pourcentage de quota.
  useEffect(() => {
    if (creditsBalance === null) {
      dispatch(fetchCreditBalance());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  if (!quota) return null;

  const pct = Math.max(0, Math.min(100, quota.percentUsed ?? 0));
  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  const resetLabel = formatResetDate(quota.resetAt, locale);
  const creditsUsd = creditsBalance?.balanceUsd ?? 0;
  // Parité d'affichage : 1 crédit-€ = 1 $ de budget interne (voir AiCreditsSection).
  const creditsLabel =
    creditsUsd > 0
      ? new Intl.NumberFormat(locale, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 2,
        }).format(creditsUsd)
      : null;

  // Avec des crédits disponibles, pas d'alarme : le quota inclus peut être
  // plein sans qu'aucun blocage ne menace.
  const barColor =
    creditsUsd > 0
      ? "bg-emerald-500 dark:bg-emerald-400"
      : pct >= 90
        ? "bg-red-500 dark:bg-red-400"
        : pct >= 60
          ? "bg-amber-500 dark:bg-amber-400"
          : "bg-emerald-500 dark:bg-emerald-400";

  // Inline variant — compact pill for the chat header
  if (variant === "inline") {
    return (
      <div
        className="flex flex-col items-end gap-2.5 shrink-0"
        title={t("aiAgent.quota.title")}
      >
        <div
          className="w-56 h-2 overflow-hidden rounded-full bg-muted/60 dark:bg-muted/40 shrink-0"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t("aiAgent.quota.ariaLabel", { pct })}
        >
          <div
            className={cn(
              "h-full transition-all duration-300 ease-out",
              barColor,
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs tabular-nums font-medium text-muted-foreground whitespace-nowrap">
          {t("aiAgent.quota.used", { pct })}
          {creditsLabel && (
            <span className="text-muted-foreground/70">
              {" "}
              · {t("aiCredits.inlineBalance", { amount: creditsLabel })}
            </span>
          )}
        </span>
      </div>
    );
  }

  // Banner variant — full-width strip (used on Subscription page)
  return (
    <div
      className="px-4 py-2 border-b bg-muted/20"
      title={t("aiAgent.quota.title")}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-medium text-muted-foreground">
          {t("aiAgent.quota.monthly")}
          {creditsLabel && (
            <span className="text-muted-foreground/70">
              {" "}
              · {t("aiCredits.inlineBalance", { amount: creditsLabel })}
            </span>
          )}
        </span>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {t("aiAgent.quota.used", { pct })}
          {resetLabel && (
            <span className="text-muted-foreground/70">
              {" "}
              · {t("aiAgent.quota.resetOn", { date: resetLabel })}
            </span>
          )}
          <button
            type="button"
            onClick={() => navigate("/dashboard/subscription")}
            className="ml-2 cursor-pointer font-medium text-primary hover:underline"
          >
            {t("aiCredits.buyCta")}
          </button>
        </span>
      </div>
      <div
        className={cn(
          "mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted/60 dark:bg-muted/40",
        )}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t("aiAgent.quota.ariaLabel", { pct })}
      >
        <div
          className={cn(
            "h-full transition-all duration-300 ease-out",
            barColor,
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
